<template>
  <div
    v-if="visible && resolvedPlacements.length > 0"
    class="pointer-events-none absolute inset-0 z-[25]"
    aria-hidden="true"
  >
    <div
      v-for="item in resolvedPlacements"
      :key="item.placement.id"
      class="absolute" :class="[CTA_QR_SURFACE_CLASS]"
      :style="item.style"
    >
      <div
        :ref="(el) => setQrEl(item.placement.id, el)"
        class="qrcode-block size-full"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ICtaUrlInterpolationContext } from '@lucimark/shared/helpers/substituteCtaUrlTemplate'
import type { TCtaRegistryEntry } from '@lucimark/shared/models/cta'
import type { TLayout } from '@lucimark/shared/models/playlist'
import type { IQueueFrame } from '@/types'
import {
  CTA_QR_CODE_RENDER_OPTIONS_PLAYER,
  CTA_QR_SURFACE_CLASS
} from '@lucimark/shared/helpers/ctaQrCodeOptions'
import { ctaPlacementToPercentStyle } from '@lucimark/shared/helpers/frameCtaLayoutStyle'
import {
  assertValidCtaUrlAfterSubstitution,
  substituteCtaUrlTemplate
} from '@lucimark/shared/helpers/substituteCtaUrlTemplate'
import QRCode from 'qrcode'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps<{
  frame: IQueueFrame
  layout: TLayout
  ctaRegistry: Record<string, TCtaRegistryEntry>
  interpolationContext: ICtaUrlInterpolationContext
  visible: boolean
}>()

const qrEls = new Map<string, HTMLElement>()
const qrSvgByPlacement = ref<Record<string, string>>({})

const resolvedPlacements = computed(() => {
  const out: Array<{
    placement: NonNullable<IQueueFrame['ctas']>[number]
    style: Record<string, string>
    url: string
  }> = []
  for (const placement of props.frame.ctas ?? []) {
    const entry = props.ctaRegistry[placement.ctaId]
    if (!entry?.url) {
      continue
    }
    try {
      const url = substituteCtaUrlTemplate(entry.url, props.interpolationContext)
      assertValidCtaUrlAfterSubstitution(url)
      out.push({
        placement,
        style: ctaPlacementToPercentStyle(placement, props.layout),
        url
      })
    }
    catch {
      // skip invalid URL after substitution
    }
  }
  return out
})

function injectQrSvg(id: string): void {
  const el = qrEls.get(id)
  const svg = qrSvgByPlacement.value[id]
  if (el && svg) {
    el.innerHTML = svg
  }
}

function injectAllQrSvgs(): void {
  for (const id of qrEls.keys()) {
    injectQrSvg(id)
  }
}

function setQrEl(id: string, el: unknown): void {
  if (el instanceof HTMLElement) {
    qrEls.set(id, el)
    injectQrSvg(id)
  }
  else {
    qrEls.delete(id)
  }
}

async function refreshQrCodes(): Promise<void> {
  const next: Record<string, string> = {}
  for (const item of resolvedPlacements.value) {
    try {
      next[item.placement.id] = await QRCode.toString(item.url, CTA_QR_CODE_RENDER_OPTIONS_PLAYER)
    }
    catch {
      next[item.placement.id] = ''
    }
  }
  qrSvgByPlacement.value = next
  await nextTick()
  injectAllQrSvgs()
}

watch(resolvedPlacements, () => {
  void refreshQrCodes()
}, { immediate: true })

watch(qrSvgByPlacement, () => {
  injectAllQrSvgs()
}, { deep: true })

onBeforeUnmount(() => {
  qrEls.clear()
})
</script>

<style scoped>
.qrcode-block :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
